import { Button, EmptyState, Modal } from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';

import { QuerySuspense } from 'common/components/QuerySuspense';
import { useTranslation } from 'common/hooks/useTranslation';
import { NotificationType, useNotifications } from 'modules/app/notifications';
import { useCostCentersQuery } from 'modules/budgets/apis';
import { useQueryStates } from 'src/core/api/hooks/useQueryStates';

import { EcBulkAssignModalContent } from './EcBulkAssignModalContent';
import { type ExpenseCategory } from '../../expenseCategory';
import { useBulkAssignCostCenters } from '../../hooks/useBulkAssignCostCenters';

type Props = {
  expenseCategories: ExpenseCategory[];
  isOpen: boolean;
  onComplete(): void;
  onCancel(): void;
};

export const EcBulkAssignModalContainer = ({
  expenseCategories,
  isOpen,
  onComplete,
  onCancel,
}: Props) => {
  const { t } = useTranslation('global');
  const { dangerNotif, successNotif, pushNotif } = useNotifications();
  const bulkAssignCostCenters = useBulkAssignCostCenters();

  const [costCenterAssignationType, setCostCenterAssignationType] = useState<
    'all' | 'some'
  >('all');
  const [costCenterIds, setCostCenterIds] = useState<string[]>([]);

  const queryStates = useQueryStates({
    states: {
      costCenters: useCostCentersQuery(),
    },
    reshapeData({ costCenters }) {
      return { costCenters };
    },
  });

  const handleSubmit = async () => {
    // Validate
    if (costCenterAssignationType === 'some' && costCenterIds.length === 0) {
      return;
    }

    try {
      const finalCostCenterIds =
        costCenterAssignationType === 'all' ? [] : costCenterIds;
      const { failedCategories } = await bulkAssignCostCenters(
        expenseCategories,
        finalCostCenterIds,
      );

      if (failedCategories.length === 0) {
        successNotif(
          t('expenseCategories.bulkAssign.success', {
            count: expenseCategories.length,
          }),
        );
      } else if (failedCategories.length === expenseCategories.length) {
        dangerNotif(t('expenseCategories.bulkAssign.error'));
        return;
      } else {
        const failedNames = failedCategories.map((cat) => cat.name).join(', ');
        const successCount = expenseCategories.length - failedCategories.length;
        pushNotif({
          type: NotificationType.Danger,
          message: t('expenseCategories.bulkAssign.partialSuccess', {
            successCount,
            failedCount: failedCategories.length,
            failedNames,
          }),
          canExpire: false,
        });
      }

      // Reset form
      setCostCenterAssignationType('all');
      setCostCenterIds([]);

      onComplete();
    } catch (error) {
      dangerNotif(t('expenseCategories.bulkAssign.error'));
      throw error;
    }
  };

  const handleCancel = () => {
    // Reset form
    setCostCenterAssignationType('all');
    setCostCenterIds([]);
    onCancel();
  };

  const handleCostCenterAssignationTypeChange = (value: 'all' | 'some') => {
    setCostCenterAssignationType(value);
  };

  const handleCostCenterIdsChange = (ids: string[]) => {
    setCostCenterIds(ids);
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const firstCategoryCostCenterIds = expenseCategories[0].costCenterIds || [];
    const allHaveSameCostCenters = expenseCategories.every((category) => {
      const categoryCostCenterIds = category.costCenterIds || [];

      if (categoryCostCenterIds.length !== firstCategoryCostCenterIds.length) {
        return false;
      }

      const sortedCategory = [...categoryCostCenterIds].sort();
      const sortedFirst = [...firstCategoryCostCenterIds].sort();

      return sortedCategory.every((id, index) => id === sortedFirst[index]);
    });

    if (allHaveSameCostCenters && firstCategoryCostCenterIds.length > 0) {
      setCostCenterAssignationType('some');
      setCostCenterIds(firstCategoryCostCenterIds);
    } else {
      setCostCenterAssignationType('all');
      setCostCenterIds([]);
    }
  }, [isOpen, expenseCategories]);

  return (
    <QuerySuspense
      queryState={queryStates}
      fallback={() => (
        <EmptyState
          title={t('misc.errors.networkError')}
          subtitle=""
          iconVariant="alert"
          iconName="hexagone-cross"
        />
      )}
      loading={null}
    >
      {({ costCenters }) => (
        <Modal
          isOpen={isOpen}
          iconName="tag"
          iconVariant="info"
          title={t('expenseCategories.bulkAssign.title')}
          actions={[
            <Button
              key="cancel"
              variant="secondaryNeutral"
              text={t('expenseCategories.form.cancel')}
              onClick={handleCancel}
            />,
            <Button
              key="save"
              variant="primaryBrand"
              text={t('expenseCategories.bulkAssign.confirm')}
              onClick={handleSubmit}
            />,
          ]}
        >
          <EcBulkAssignModalContent
            costCenters={costCenters}
            expenseCategories={expenseCategories}
            costCenterAssignationType={costCenterAssignationType}
            onCostCenterAssignationTypeChange={
              handleCostCenterAssignationTypeChange
            }
            costCenterIds={costCenterIds}
            onCostCenterIdsChange={handleCostCenterIdsChange}
          />
        </Modal>
      )}
    </QuerySuspense>
  );
};
